import { Component, Host, h, Listen, Prop, State, Event, EventEmitter, Watch, Method, AttachInternals } from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import flagsSrc from 'intl-tel-input/build/img/flags@2x.png';
import { EventDetail, stopEvent } from '../../utils';

const flagStyles = {
  backgroundImage: `url(${flagsSrc})`,
};

export interface Country {
  name: string;
  iso2: string;
  dialCode: number;
  priority: number;
  areaCodes: string;
}

let selCountryData: Country = null;
@Component({
  tag: 'anchor-phone',
  styleUrl: 'phone.scss',
  shadow: true,
  formAssociated: true,
})
export class Phone {
  selectButton!: HTMLButtonElement;

  @AttachInternals() internals: ElementInternals;

  // eslint-disable-next-line no-undef
  anchorInput!: HTMLAnchorInputElement;

  /**
   *  Give this instance of phone a custom ID, or keep the default.
   */
  @Prop({}) uuid: string = uuidv4();

  /**
   * The value in the number field. Use this to set a value
   */
  @Prop() value: string = '';

  /**
   * The name of the number field
   */
  @Prop() name: string = '';

  /**
   * Placeholder text is shown until the user interacts with the number field
   */
  @Prop() placeholder: string = '';

  /**
   * Set the field to be read only
   */
  @Prop() readonly: boolean = false;

  /**
   * Disable the number field
   */
  @Prop() disabled: boolean = false;

  /**
   * Show an error message
   */
  @Prop() error: string = '';

  /**
   * Show a warning message
   */
  @Prop() warning: string = '';

  /**
   * Show a help text
   */
  @Prop() helpText: string = '';

  /**
   * Show the label (you can't currently disable the label)
   */
  @Prop() label: boolean = true;

  /**
   * Placement of the label
   */
  @Prop() labelPlacement: 'top' | 'left' = 'top';

  /**
   * Set a fixed width in characters for the number field.
   */
  @Prop() size: number = null;

  /**
   * Set a max width in characters for the number field.
   */
  @Prop() maxLength: number;

  /**
   * Add an info icon with a tooltip to the right of the label
   */
  @Prop() tooltipText: string = '';

  /**
   * Show a checkmark icon when the input field has been succesfully filled
   */
  @Prop() filled: boolean = false;

  @Prop() spinners: 'none' | 'small' | 'big' = 'none';

  /**
   * Pass an array of countries to the phone field
   */
  @Prop({ mutable: true }) options: string | Array<Country>;

  @Prop({ mutable: true }) selectedCountry: number = 0;

  @Prop() pattern: string = null;

  /**
   *  Event emitted when a country is selected
   */
  @Event() countrySelected: EventEmitter;

  /**
   * Event emitted when the user is inputting something into the input field
   */
  @Event() inputevent: EventEmitter<EventDetail>;

  /**
   * Event emitted when the user is focusing the input field
   */
  @Event() focusevent: EventEmitter;

  /**
   * Event emitted when the user blurs the input field
   */
  @Event() blurevent: EventEmitter;

  /**
   * Event emitted when the user clears the input field
   */
  @Event() clearevent: EventEmitter;

  /**
   *  Event emitted when user inputs something into the field
   */
  @Event() inputChange: EventEmitter<EventDetail>;

  /**
   *  Event emitted when the input matches the pattern
   */
  @Event() phoneNumberValid: EventEmitter<boolean>;

  /**
   *  Event emitted when the input does not match the pattern
   */
  @Event() phoneNumberInvalid: EventEmitter;

  @State() parsedOptions: Array<Country>;

  @State() hasFocus: boolean = false;

  @State() expanded: boolean = false;

  @State() focusedItem: number = -1;

  @State() isHovered: boolean = false;

  @Listen('focusin')
  focusInHandler() {
    this.hasFocus = true;
  }

  @Listen('focusout')
  focusOutHandler() {
    this.expanded = false;
    this.hasFocus = false;
  }

  @Listen('inputChange')
  handleInputChange(value) {
    this.checkValidPhoneNumber(value.detail.value);
  }

  @Listen('next')
  handleNext(e) {
    stopEvent(e);
    return this.expanded ? this.setFocus(this.focusedItem + 1) : this.expandAndFocus();
  }

  @Listen('previous')
  handlePrevious(e) {
    stopEvent(e);
    return this.expanded ? this.setFocus(this.focusedItem - 1) : this.expandAndFocus();
  }

  @Watch('expanded')
  setFocusToInputWhenClosed(expanded) {
    if (!expanded) {
      this.anchorInput.focusInput();
    }
  }

  @Method()
  async checkValidPhoneNumber(value) {
    const inputValid = value => value.match(this.pattern);

    const emitValid = () => this.phoneNumberValid.emit(value);
    const emitInvalid = () => this.phoneNumberInvalid.emit(value);
    return (!this.disabled && inputValid(value) && emitValid()) || (!this.disabled && !inputValid(value) && emitInvalid());
  }

  setSelected(index: number) {
    this.selectedCountry = index;
    this.countrySelected.emit({
      target: {
        value: this.options[this.selectedCountry],
      },
    });
    this.toggleSelect();
    this.focusedItem = -1;
  }

  toggleHover = () => {
    this.isHovered = !this.isHovered;
  };

  toggleSelect = () => {
    this.expanded = !this.expanded;
  };

  setFocus(index: number = undefined) {
    this.focusedItem = index;
    console.log('setFocus', index)
    return this.focusFirst() && this.focusLast();
  }

  expandAndFocus() {
    const index = Number.isNaN(this.selectedCountry) || !this.selectedCountry ? 0 : this.selectedCountry;
    this.toggleSelect();
    this.setFocus(index);
  }

  async focusFirst() {
    const isBeforeFirst = this.focusedItem < 0;
    const setFocusOnFirstItem = () => {
      this.focusedItem = 0;
      return true;
    };
    return isBeforeFirst && setFocusOnFirstItem();
  }

  async focusLast() {
    const lastIndex = this.options.length - 1;
    const isAfterLast = this.focusedItem > lastIndex;
    const setFocusOnLastItem = () => {
      this.focusedItem = lastIndex;
      return true;
    };
    return isAfterLast && setFocusOnLastItem();
  }

  handleCountrySelectToggle(e: MouseEvent) {
    stopEvent(e);
    return !this.expanded ? this.expandAndFocus(): this.toggleSelect();
  }

  handleOptionKeyPress = (e: KeyboardEvent) => {
    switch (e.code) {
      case 'Escape':
        this.toggleSelect();
        break;      
      default:
        break;
    }
  };

  handleButtonKeyPress = (e: KeyboardEvent) => {
    switch (e.code) {
      case 'Space':
      case 'Enter':
      case 'ArrowDown':
      case 'ArrowUp':
        if (!this.expanded) {
          stopEvent(e);
          this.expandAndFocus();
        } else {
          this.setFocus(0)
        }
        break;
      default:
        break;
    }
  };

  handleCountrySelectSelection = (e, index) => {
    stopEvent(e);
    this.setSelected(index);
  };

  componentWillRender() {
    if (typeof this.options === 'string') {
      this.parsedOptions = JSON.parse(this.options);
      return;
    }
    this.parsedOptions = this.options;
  }

  render() {
    const flagClass = 'iti__flag iti__';
    const countryListClass = { 'anchor-c-phone__country-list': true, 'open': this.expanded };
    const isFocused = index => this.focusedItem === index;
    if (this.parsedOptions) {
      selCountryData = this.parsedOptions[this.selectedCountry];
    }

    const id = this.uuid;

    return (
      <Host>
        <div class={{ 'anchor-c-phone__flag-container': true, 'open': this.expanded }}>
          <anchor-input
            class={{
              'anchor-c-phone': true,
              'focused': this.hasFocus && !this.expanded,
              'error': !!this.error,
              'disabled': this.disabled,
              'hover': this.isHovered,
            }}
            disabled={this.disabled}
            errorProp={this.error}
            filled={this.filled}
            help-text={this.helpText}
            input-id={id}
            maxLength={this.maxLength}
            name={this.name}
            onMouseEnter={() => this.toggleHover()}
            onMouseLeave={() => this.toggleHover()}
            onKeyDown={e => this.handleButtonKeyPress(e)}
            pattern={this.pattern}
            placeholder={this.placeholder}
            readonly={this.readonly}
            size={this.size}
            value={this.value}
            warning={this.warning}
            ref={el => {
              // eslint-disable-next-line no-undef
              this.anchorInput = el as HTMLAnchorInputElement;
            }}
          >
            <slot />
            <div slot="prefix">
              <button
                class={{ 'selected-flag': true, 'disabled': this.disabled, 'error': !!this.error, 'warning': !!this.warning, 'focused': this.hasFocus && !this.expanded }}
                onClick={e => this.handleCountrySelectToggle(e)}
                onKeyDown={e => this.handleButtonKeyPress(e)}
                tabindex={0}
                ref={el => {
                  this.selectButton = el as HTMLButtonElement;
                }}
                disabled={this.disabled}
                role="combobox"
                aria-controls={`country-list-${id}`}
                aria-haspopup="listbox"
                aria-expanded={this.expanded}
                aria-label="Select country"
              >
                {selCountryData && (
                  <div class="iti__flag-box">
                    <div class={flagClass + selCountryData.iso2} style={flagStyles}></div>
                  </div>
                )}
                {selCountryData && <span class="iti__dial-code anchor-c-phone__selected-dialcode">+{selCountryData.dialCode}</span>}
                <anchor-icon name="chevron-down" size="xsmall" />
              </button>
            </div>
          </anchor-input>
          <div
            class={countryListClass}
            aria-hidden={!this.expanded}
            role="listbox"
            id={`country-list-${id}`}
          >
            {this.parsedOptions &&
              this.parsedOptions.map((country, index) => (
                <anchor-select-item
                  class={{ 'anchor-c-phone__country-list-item': true, 'focused': isFocused(index) }}
                  focused={isFocused(index)}
                  onPressed={e => this.handleCountrySelectSelection(e, index)}
                  tabIndex={0}
                  disabled={this.disabled}
                  role="option"
                  onKeyDown={this.handleOptionKeyPress}
                >
                  <anchor-country-code name={country.name} iso={country.iso2} dial-code={country.dialCode}></anchor-country-code>
                </anchor-select-item>
              ))}
          </div>
        </div>
      </Host>
    );
  }
}
